import { useEffect, useState } from 'react';
import { Loader2, Pencil, Plus, Trash2, X } from 'lucide-react';
import {
  deleteContentBlock,
  deletePresentationExample,
  fetchContentBlocks,
  fetchPresentationExamples,
  saveContentBlock,
} from '@/lib/api';
import { BLOCK_KINDS, groupBlocksByKind, kindLabel, parseTags } from '@/lib/text-library-format';
import type { ContentBlock, PresentationExample } from '@/lib/types';

type Tab = 'blokken' | 'voorbeelden';

const fieldCls =
  'w-full rounded-md border border-bmade-200 bg-white text-sm text-bmade-950 outline-none transition-colors focus:border-accent-500 dark:border-bmade-700 dark:bg-bmade-950/60 dark:text-white dark:focus:border-accent-400';

export function TextLibraryPage() {
  const [tab, setTab] = useState<Tab>('blokken');

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-bmade-950 dark:text-white">Teksten</h1>
        <p className="text-sm text-bmade-600 dark:text-bmade-400">
          Herbruikbare tekstblokken en huisstijl-voorbeelden voor de offerte-generator.
        </p>
      </header>

      <div className="inline-flex rounded-md border border-bmade-200 bg-white p-0.5 text-xs dark:border-bmade-800 dark:bg-bmade-900">
        <button
          type="button"
          onClick={() => setTab('blokken')}
          className={`rounded px-3 py-1.5 font-medium transition-colors ${
            tab === 'blokken'
              ? 'bg-accent-500 text-white'
              : 'text-bmade-600 hover:text-bmade-900 dark:text-bmade-300 dark:hover:text-white'
          }`}
        >
          Tekstblokken
        </button>
        <button
          type="button"
          onClick={() => setTab('voorbeelden')}
          className={`rounded px-3 py-1.5 font-medium transition-colors ${
            tab === 'voorbeelden'
              ? 'bg-accent-500 text-white'
              : 'text-bmade-600 hover:text-bmade-900 dark:text-bmade-300 dark:hover:text-white'
          }`}
        >
          Huisstijl-voorbeelden
        </button>
      </div>

      {tab === 'blokken' ? <ContentBlocksTab /> : <ExamplesTab />}
    </div>
  );
}

// === Tekstblokken (content_blocks) ===

function ContentBlocksTab() {
  const [blocks, setBlocks] = useState<ContentBlock[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<ContentBlock | 'new' | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setBlocks(await fetchContentBlocks());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Laden mislukt');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function handleDelete(id: string) {
    if (!window.confirm('Dit tekstblok verwijderen?')) return;
    try {
      await deleteContentBlock(id);
      setBlocks((prev) => prev.filter((b) => b.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verwijderen mislukt');
    }
  }

  const groups = groupBlocksByKind(blocks);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-bmade-500">
          Evergreen-teksten die terugkomen in offertes. Goede doelen kies je per pakket.
        </p>
        <button
          type="button"
          onClick={() => setEditing('new')}
          className="inline-flex items-center gap-2 rounded-md bg-accent-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-600"
        >
          <Plus className="h-3 w-3" />
          Nieuw blok
        </button>
      </div>

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-bmade-500">Laden...</div>
      ) : (
        <div className="space-y-6">
          {groups.map((group) => (
            <section key={group.kind} className="space-y-2">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-bmade-500">
                {group.label} ({group.blocks.length})
              </h2>
              {group.blocks.length === 0 ? (
                <p className="text-xs text-bmade-400">Nog geen blokken.</p>
              ) : (
                <div className="space-y-2">
                  {group.blocks.map((block) => (
                    <div
                      key={block.id}
                      className="rounded-lg border border-bmade-200 bg-white p-3 dark:border-bmade-800 dark:bg-bmade-900/40"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="truncate text-sm font-medium text-bmade-950 dark:text-white">
                              {block.title}
                            </span>
                            {!block.active && (
                              <span className="rounded bg-bmade-200 px-1.5 py-0.5 text-[10px] font-medium text-bmade-600 dark:bg-bmade-800 dark:text-bmade-400">
                                inactief
                              </span>
                            )}
                          </div>
                          <p className="mt-1 line-clamp-3 whitespace-pre-wrap text-xs text-bmade-600 dark:text-bmade-400">
                            {block.body}
                          </p>
                          {block.tags && block.tags.length > 0 && (
                            <div className="mt-1.5 flex flex-wrap gap-1">
                              {block.tags.map((t) => (
                                <span
                                  key={t}
                                  className="rounded bg-accent-500/10 px-1.5 py-0.5 text-[10px] text-accent-700 dark:text-accent-300"
                                >
                                  {t}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex shrink-0 gap-1">
                          <button
                            type="button"
                            onClick={() => setEditing(block)}
                            className="rounded-md border border-bmade-200 p-1.5 text-bmade-600 transition-colors hover:border-accent-400 hover:text-accent-700 dark:border-bmade-700 dark:text-bmade-400"
                            title="Bewerken"
                          >
                            <Pencil className="h-3 w-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => void handleDelete(block.id)}
                            className="rounded-md border border-bmade-200 p-1.5 text-bmade-600 transition-colors hover:border-red-400 hover:text-red-600 dark:border-bmade-700 dark:text-bmade-400"
                            title="Verwijderen"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>
      )}

      {editing && (
        <BlockEditor
          block={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

function BlockEditor({
  block,
  onClose,
  onSaved,
}: {
  block: ContentBlock | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [kind, setKind] = useState<ContentBlock['kind']>(block?.kind ?? 'goed_doel');
  const [title, setTitle] = useState(block?.title ?? '');
  const [body, setBody] = useState(block?.body ?? '');
  const [tagsInput, setTagsInput] = useState((block?.tags ?? []).join(', '));
  const [active, setActive] = useState(block?.active ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    if (!title.trim() || !body.trim()) {
      setError('Titel en tekst zijn verplicht.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await saveContentBlock({
        id: block?.id,
        kind,
        title: title.trim(),
        body: body.trim(),
        tags: parseTags(tagsInput),
        active,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Opslaan mislukt');
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-xl border border-bmade-200 bg-white p-5 shadow-xl dark:border-bmade-800 dark:bg-bmade-900">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-bmade-950 dark:text-white">
            {block ? 'Tekstblok bewerken' : 'Nieuw tekstblok'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-bmade-500 transition-colors hover:bg-bmade-100 dark:hover:bg-bmade-800"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-bmade-700 dark:text-bmade-300">
              Soort
            </span>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as ContentBlock['kind'])}
              className={`${fieldCls} px-2 py-1.5`}
            >
              {BLOCK_KINDS.map(({ kind: k }) => (
                <option key={k} value={k}>
                  {kindLabel(k)}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1 block text-xs font-medium text-bmade-700 dark:text-bmade-300">
              Titel
            </span>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Bv. Treebytree"
              className={`${fieldCls} px-2 py-1.5`}
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-xs font-medium text-bmade-700 dark:text-bmade-300">
              Tekst
            </span>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={7}
              placeholder="De vaste tekst van dit blok..."
              className={`${fieldCls} resize-y px-3 py-2 leading-relaxed`}
              spellCheck
              lang="nl"
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-xs font-medium text-bmade-700 dark:text-bmade-300">
              Tags (komma-gescheiden, optioneel)
            </span>
            <input
              type="text"
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              placeholder="duurzaam, fries, kerst"
              className={`${fieldCls} px-2 py-1.5`}
            />
          </label>

          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              className="h-4 w-4 rounded border-bmade-300 text-accent-500 focus:ring-accent-500 dark:border-bmade-600"
            />
            <span className="text-xs font-medium text-bmade-700 dark:text-bmade-300">
              Actief (zichtbaar in de keuzelijst bij genereren)
            </span>
          </label>

          {error && (
            <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300">
              {error}
            </div>
          )}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-bmade-200 px-3 py-1.5 text-xs font-medium text-bmade-700 transition-colors hover:bg-bmade-100 dark:border-bmade-700 dark:text-bmade-300 dark:hover:bg-bmade-800"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-md bg-accent-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-600 disabled:opacity-40"
          >
            {saving && <Loader2 className="h-3 w-3 animate-spin" />}
            Opslaan
          </button>
        </div>
      </div>
    </div>
  );
}

// === Huisstijl-voorbeelden (presentation_examples) ===

function ExamplesTab() {
  const [examples, setExamples] = useState<PresentationExample[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setExamples(await fetchPresentationExamples());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Laden mislukt');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function handleDelete(id: string) {
    if (!window.confirm('Dit voorbeeld verwijderen?')) return;
    try {
      await deletePresentationExample(id);
      setExamples((prev) => prev.filter((e) => e.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verwijderen mislukt');
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-bmade-500">
        Goedgekeurde teksten die de generator gebruikt als toon-referentie. Toevoegen doe je vanuit
        een presentatie met de knop "Bewaar als huisstijl-voorbeeld".
      </p>

      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-bmade-500">Laden...</div>
      ) : examples.length === 0 ? (
        <p className="text-sm text-bmade-500">Nog geen huisstijl-voorbeelden.</p>
      ) : (
        <div className="space-y-2">
          {examples.map((ex) => (
            <div
              key={ex.id}
              className="rounded-lg border border-bmade-200 bg-white p-3 dark:border-bmade-800 dark:bg-bmade-900/40"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-bmade-950 dark:text-white">
                      {ex.title}
                    </span>
                    {ex.theme && (
                      <span className="rounded bg-accent-500/10 px-1.5 py-0.5 text-[10px] text-accent-700 dark:text-accent-300">
                        {ex.theme}
                      </span>
                    )}
                  </div>
                  {ex.source && (
                    <div className="mt-0.5 text-[11px] text-bmade-500">Bron: {ex.source}</div>
                  )}
                  <p className="mt-1 line-clamp-4 whitespace-pre-wrap text-xs text-bmade-600 dark:text-bmade-400">
                    {ex.body}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => void handleDelete(ex.id)}
                  className="shrink-0 rounded-md border border-bmade-200 p-1.5 text-bmade-600 transition-colors hover:border-red-400 hover:text-red-600 dark:border-bmade-700 dark:text-bmade-400"
                  title="Verwijderen"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
